import config from './config.js';
import router from './router.js';
import stateManager from './state.js';
import eventBus from './eventBus.js';
import api from './api.js';
import idleTimer from './idleTimer.js';
import '../utils/notify.js';
// Side-effect import: exposes window.heroicon for Tabulator formatters
// and other template-literal HTML that can't easily use ES imports.
import '../utils/heroicons.js';

// Register dayjs plugins. Modules run after deferred classic scripts, so
// by the time this executes, dayjs and all plugins have loaded onto
// window. relativeTime powers "2 hours ago" labels (NotificationDropdown,
// CommentsSection, etc). utc+timezone power formatDate()'s UTC -> user's-
// local-timezone conversion in utils/helpers.js — utc must be extended
// before timezone (the timezone plugin depends on it).
if (window.dayjs && window.dayjs_plugin_relativeTime) {
  window.dayjs.extend(window.dayjs_plugin_relativeTime);
}
if (window.dayjs && window.dayjs_plugin_utc && window.dayjs_plugin_timezone) {
  window.dayjs.extend(window.dayjs_plugin_utc);
  window.dayjs.extend(window.dayjs_plugin_timezone);
}
import { applySidebarPermissions, canAccessRoute, getLandingRoute } from './permissions.js';
import { setDefaultCurrency, accessDeniedHTML } from '../utils/helpers.js';
import { loadEnabledCurrencies } from './currencies.js';
import { initSidebarSections } from './sidebarSections.js';
import NotificationDropdown from '../components/NotificationDropdown.js';
import WelcomeWizard from '../components/WelcomeWizard.js';
import GlobalSearchBar from '../components/GlobalSearchBar.js';
import favoriteStar from '../components/FavoriteStar.js';

// Expose for the user-menu "Take the tour" link in index.html
window.PM_showTour = () => WelcomeWizard.show(router);
// Data import is now a dedicated page (/import), not a modal.
window.PM_showImport = () => router.navigate('/import');

// Application initialization
class App {
  constructor() {
    this.init();
  }

  async init() {
    // Dev-only init noise — silent in production so the browser console
    // is left clean for real diagnostics.
    const isDev = /^(localhost|127\.0\.0\.1|::1)$/.test(location.hostname);
    if (isDev) console.log('🚀 Initializing Project Management App...');

    // Setup event listeners
    this.setupEventListeners();

    // Register routes
    this.registerRoutes();

    // Check authentication
    await this.checkAuth();

    // Re-evaluate layout after auth resolves — handles the stale/expired
    // token case where isAuthenticated started true from localStorage but
    // was cleared by a failed /auth/me call.
    this.updateLayoutVisibility();

    // Wire up collapsible sidebar sections. Must run after permissions
    // have applied any disabled/hidden states so we group only the
    // actually-rendered items.
    initSidebarSections();

    // Initialize router
    router.init();

    if (isDev) console.log('✅ App initialized successfully');
  }

  // Setup global event listeners
  setupEventListeners() {
    // Intercept clicks on access-disabled nav links so they don't
    // navigate. Capture-phase so we stop the router's own listener.
    document.addEventListener('click', (e) => {
      const disabled = e.target.closest('.nav-link--disabled, [data-access-disabled]');
      if (disabled) {
        e.preventDefault();
        e.stopImmediatePropagation();
        window.notify.warning("You don't have permission to access this feature. Please contact your admin.");
      }
    }, true);

    // Hide/show sidebar and header based on auth state
    this.updateLayoutVisibility();

    // Listen to route changes to update layout
    eventBus.on('route:changed', () => {
      this.updateLayoutVisibility();
    });

    // Logout event
    eventBus.on('auth:logout', () => {
      idleTimer.stop();
      stateManager.clearAuth();
      api.success('Logged out successfully');
      router.navigate('/login');
    });

    // 403 Forbidden from any API call renders the access-denied page in the
    // main content area instead of surfacing a toast.
    eventBus.on('auth:forbidden', (message) => {
      const mainContent = document.getElementById('main-content');
      if (mainContent) {
        mainContent.innerHTML = accessDeniedHTML(message);
      }
    });

    // Idle auto-logout: fired by idleTimer after the configured period of
    // inactivity. Same teardown as a manual logout, but a distinct message
    // so the user understands why they were signed out (and that login
    // itself still works — they just need to sign back in).
    eventBus.on('auth:idle-logout', () => {
      stateManager.clearAuth();
      api.error('You were logged out due to inactivity. Please sign in again.');
      router.navigate('/login');
    });

    // Start/stop the idle-logout watcher whenever auth state flips. This is
    // the single wiring point — covers boot (checkAuth), fresh login
    // (setAuth), and logout/expiry (clearAuth) alike.
    stateManager.subscribe('isAuthenticated', (isAuth) => {
      if (isAuth) idleTimer.start();
      else idleTimer.stop();
    });

    // Logout button
    document.getElementById('logout-btn')?.addEventListener('click', (e) => {
      e.preventDefault();
      eventBus.emit('auth:logout');
    });

    // Replay first-run tour
    document.getElementById('take-tour-btn')?.addEventListener('click', (e) => {
      e.preventDefault();
      WelcomeWizard.show(router);
    });

    // Open the CSV import page
    document.getElementById('import-data-btn')?.addEventListener('click', (e) => {
      e.preventDefault();
      router.navigate('/import');
    });

    // User menu toggle
    const userMenuBtn = document.getElementById('user-menu-btn');
    const userMenuDropdown = document.getElementById('user-menu-dropdown');

    if (userMenuBtn && userMenuDropdown) {
      const setUserMenuOpen = (open) => {
        userMenuDropdown.classList.toggle('hidden', !open);
        userMenuBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      };

      userMenuBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const willOpen = userMenuDropdown.classList.contains('hidden');
        setUserMenuOpen(willOpen);
      });

      // Close dropdown when clicking outside
      document.addEventListener('click', () => {
        setUserMenuOpen(false);
      });

      // Escape closes the menu and returns focus to the trigger so
      // keyboard users aren't stranded inside a closed dropdown.
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !userMenuDropdown.classList.contains('hidden')) {
          setUserMenuOpen(false);
          userMenuBtn.focus();
        }
      });
    }

    // Mobile menu — drawer-style sidebar with scrim, scroll lock, ESC,
    // route-change auto-close, and focus management.
    const mobileMenuBtn = document.getElementById('mobile-menu-btn');
    const sidebar = document.getElementById('sidebar');
    const sidebarBackdrop = document.getElementById('sidebar-backdrop');

    const isMobile = () => window.matchMedia('(max-width: 767px)').matches;

    const openMobileSidebar = () => {
      if (!sidebar || !mobileMenuBtn) return;
      sidebar.classList.remove('hidden');
      sidebarBackdrop?.classList.remove('hidden');
      // Prevent the page underneath from scrolling while the drawer is
      // open — otherwise touch drags on the scrim bleed through.
      document.body.style.overflow = 'hidden';
      mobileMenuBtn.setAttribute('aria-expanded', 'true');
      // Move focus into the drawer so screen-reader / keyboard users
      // land on the first nav item rather than the menu button.
      const firstLink = sidebar.querySelector('.nav-link');
      firstLink?.focus();
    };

    const closeMobileSidebar = () => {
      if (!sidebar || !mobileMenuBtn) return;
      if (sidebar.classList.contains('hidden')) return;
      sidebar.classList.add('hidden');
      sidebarBackdrop?.classList.add('hidden');
      document.body.style.overflow = '';
      mobileMenuBtn.setAttribute('aria-expanded', 'false');
      // Only restore focus to the trigger on mobile — on desktop the
      // sidebar is permanently visible and this code path runs on
      // route-change as a no-op safety net.
      if (isMobile()) mobileMenuBtn.focus();
    };

    if (mobileMenuBtn && sidebar) {
      mobileMenuBtn.addEventListener('click', () => {
        if (sidebar.classList.contains('hidden')) {
          openMobileSidebar();
        } else {
          closeMobileSidebar();
        }
      });
    }

    sidebarBackdrop?.addEventListener('click', closeMobileSidebar);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isMobile() && sidebar && !sidebar.classList.contains('hidden')) {
        closeMobileSidebar();
      }
    });

    // Auto-close the drawer after the user navigates — otherwise it
    // covers the destination page until they manually dismiss it.
    eventBus.on('route:changed', () => {
      if (isMobile()) closeMobileSidebar();
    });

    // Resizing past md while the drawer is open would leave the scroll
    // lock and scrim hanging — release them when we cross into desktop.
    window.matchMedia('(max-width: 767px)').addEventListener('change', (e) => {
      if (!e.matches) {
        sidebarBackdrop?.classList.add('hidden');
        document.body.style.overflow = '';
        mobileMenuBtn?.setAttribute('aria-expanded', 'false');
      }
    });

    // Mobile bottom navigation — active state + menu button wiring
    const updateMobileBottomNav = () => {
      const path = window.location.pathname;
      document.querySelectorAll('#mobile-bottom-nav .mobile-nav-item[data-route]').forEach(el => {
        const route = el.getAttribute('data-route');
        el.classList.toggle('is-active', !!route && (path === route || path.startsWith(route + '/')));
      });
    };

    document.getElementById('mbn-menu-btn')?.addEventListener('click', openMobileSidebar);
    eventBus.on('route:changed', updateMobileBottomNav);
    updateMobileBottomNav();

    // Sidebar collapse toggle
    this.setupSidebarCollapse();

    // Update user info in sidebar + apply role-based permissions
    stateManager.subscribe('user', (user) => {
      this.renderImpersonationBanner(user);
      if (user) {
        const userNameEl = document.getElementById('user-name');
        const userRoleEl = document.getElementById('user-role');
        if (userNameEl) {
          const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
          userNameEl.textContent = user.username ? `${fullName} (@${user.username})` : fullName;
        }
        if (userRoleEl) userRoleEl.textContent = user.role.charAt(0).toUpperCase() + user.role.slice(1);
        const headerOrgEl = document.getElementById('header-org-name');
        if (headerOrgEl && user.organization?.name) {
          headerOrgEl.textContent = user.organization.name;
        }
        // Paint the user's uploaded avatar (or initials fallback) into the
        // sidebar (bottom-left) and the header user menu (top-right). These
        // start as static placeholders in index.html, so we hydrate them here.
        this.renderUserAvatars(user);
        // Apply sidebar visibility after a tick (DOM must be ready)
        setTimeout(() => applySidebarPermissions(user.role, user.additionalAccess || [], user), 50);
        // Init notification dropdown once user is known
        if (!this._notificationDropdown) {
          this._notificationDropdown = new NotificationDropdown();
        }
        // Init global search bar — it queries /api/search which requires
        // a logged-in user, so wait for the user state to land first.
        if (!this._globalSearchBar) {
          this._globalSearchBar = new GlobalSearchBar();
        }
        // Salesforce-style favorite star — injected next to each page's h1
        if (!this._favStarInited) {
          this._favStarInited = true;
          favoriteStar.init();
        }
        // Show first-run wizard once auth + DOM are ready (skip on public/operator routes)
        if (!this._wizardChecked) {
          this._wizardChecked = true;
          setTimeout(() => {
            const path = window.location.pathname;
            const onPublic = config.publicRoutes.some(r => path === r || path.startsWith(r + '/'));
            if (!onPublic) WelcomeWizard.maybeShow(router);
          }, 600);
        }
      }
    });

    // Guard: block navigation to any route the user's role can't access.
    // Runs before the component renders so the unauthorized page is never shown,
    // even briefly. This also fixes the rapid-click bypass (each click triggers
    // a new handleRoute; the hook fires before the handler each time).
    router.beforeEach((path) => {
      const user = stateManager.get('user');
      if (!user) return;
      const landing = getLandingRoute(user.role, user);
      if (!canAccessRoute(user.role, path, user) && path !== landing && path !== '/login') {
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
          mainContent.innerHTML = accessDeniedHTML();
        }
        return false;
      }
    });

    // Re-apply sidebar permissions on every route change.
    eventBus.on('route:changed', () => {
      const user = stateManager.get('user');
      if (user) {
        applySidebarPermissions(user.role, user.additionalAccess || [], user);
      }
    });

    // Reopen TaskSidePanel from the #task=<id> hash on refresh / deep
    // link. Mirrors how Settings restores its active tab from the hash.
    eventBus.on('route:changed', async () => {
      const match = (window.location.hash || '').match(/^#task=([^&]+)/);
      if (!match) return;
      if (document.getElementById('task-side-panel')) return;
      const { default: TaskSidePanel } = await import('../components/TaskSidePanel.js');
      new TaskSidePanel({ taskId: decodeURIComponent(match[1]) });
    });
  }

  // Salesforce-style "you are logged in as X" bar. Shows whenever the
  // current /auth/me response includes `impersonatedBy` (token had `imp`
  // claim). "Return to operator" wipes the impersonation token and goes
  // back to /operator — the operator's own pm_operator_token is still
  // parked in storage from the prior session, so they land logged in.
  renderImpersonationBanner(user) {
    const existing = document.getElementById('impersonation-banner');
    const imp = user?.impersonatedBy;
    if (!imp) {
      existing?.remove();
      document.body.style.paddingTop = '';
      return;
    }
    if (existing) return; // already rendered
    const esc = (s) => String(s ?? '').replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]));
    const bar = document.createElement('div');
    bar.id = 'impersonation-banner';
    bar.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:9999;background:#facc15;color:#78350f;padding:8px 16px;display:flex;align-items:center;justify-content:space-between;font-size:13px;border-bottom:1px solid #ca8a04;';
    bar.innerHTML = `
      <div style="display:flex;align-items:center;gap:8px;">
        <i class="fas fa-user-secret"></i>
        <span><strong>Logged in as ${esc(user.firstName)} ${esc(user.lastName)}</strong> by operator
        <strong>${esc(imp.firstName)} ${esc(imp.lastName || imp.email)}</strong>. Every action is attributed to this user.</span>
      </div>
      <button id="impersonation-return"
        style="background:#78350f;color:#fff;padding:4px 12px;border-radius:4px;font-size:12px;font-weight:600;">
        <i class="fas fa-arrow-left"></i> Return to operator
      </button>
    `;
    document.body.prepend(bar);
    document.body.style.paddingTop = bar.offsetHeight + 'px';
    document.getElementById('impersonation-return').addEventListener('click', () => {
      localStorage.removeItem('pm_token');
      localStorage.removeItem('pm_impersonating');
      window.location.href = '/operator';
    });
  }

  // Hydrate the static avatar placeholders (sidebar bottom-left + header
  // top-right) with the user's uploaded image, falling back to initials.
  // The user's avatar is stored on the `avatar` field — either a data: URL
  // (base64 from the Profile upload) or an http(s)/relative URL.
  renderUserAvatars(user) {
    const esc = (s) => String(s ?? '').replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]));
    // Only allow image sources we trust — blocks javascript:/other schemes.
    const safeAvatar = (() => {
      const u = user?.avatar;
      if (!u || typeof u !== 'string') return null;
      if (/^data:image\//i.test(u)) return u;
      if (/^(https?:)?\/\//i.test(u) || u.startsWith('/')) return u;
      return null;
    })();
    const initials = `${(user?.firstName || '').charAt(0)}${(user?.lastName || '').charAt(0)}`.toUpperCase()
      || (user?.username || user?.email || '?').charAt(0).toUpperCase();
    const inner = safeAvatar
      ? `<img src="${esc(safeAvatar)}" alt="" class="w-full h-full rounded-full object-cover">`
      : esc(initials);
    ['user-avatar', 'user-menu-avatar'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.innerHTML = inner;
    });
  }

  // Setup sidebar collapse/expand toggle.
  // Two inputs decide collapsed state: the user's explicit preference
  // (sidebarCollapsedPref: 'collapsed' | 'expanded' | null) and the
  // viewport width. Below the narrow breakpoint we force-collapse so
  // pages don't get squeezed; above it we honor the user's preference
  // (or default to expanded if they've never toggled).
  setupSidebarCollapse() {
    const sidebar = document.getElementById('sidebar');
    const collapseBtn = document.getElementById('sidebar-collapse-btn');
    if (!sidebar || !collapseBtn) return;

    // Viewport width below which the sidebar is force-collapsed. 1280px
    // is Tailwind's xl breakpoint and the threshold where 13" MacBooks
    // (default scaled to 1280×800 / 1440×900) start losing content room
    // to the 224px expanded sidebar.
    const NARROW_PX = 1280;

    // Migrate legacy boolean key once. The old key only encoded
    // "collapsed = true/false"; the new one distinguishes "user
    // explicitly set this" from "no preference yet."
    const legacy = localStorage.getItem('sidebarCollapsed');
    if (legacy !== null && localStorage.getItem('sidebarCollapsedPref') === null) {
      localStorage.setItem('sidebarCollapsedPref', legacy === 'true' ? 'collapsed' : 'expanded');
      localStorage.removeItem('sidebarCollapsed');
    }

    const applyState = () => {
      const narrow = window.innerWidth < NARROW_PX;
      const pref = localStorage.getItem('sidebarCollapsedPref'); // 'collapsed' | 'expanded' | null
      // Narrow viewport forces collapse regardless of preference.
      // On wider viewports, an explicit preference wins; otherwise default expanded.
      const shouldCollapse = narrow ? true : pref === 'collapsed';
      sidebar.classList.toggle('sidebar-collapsed', shouldCollapse);
      sidebar.classList.toggle('sidebar-expanded', !shouldCollapse);
      // Disable the toggle button while we're forcing collapse so users
      // don't get a no-op click that immediately snaps back on resize.
      collapseBtn.disabled = narrow;
      collapseBtn.style.opacity = narrow ? '0.4' : '';
      collapseBtn.title = narrow ? 'Sidebar auto-collapsed on narrow screens' : 'Collapse sidebar';
    };

    applyState();

    collapseBtn.addEventListener('click', () => {
      if (window.innerWidth < NARROW_PX) return; // guard: button is disabled here anyway
      const nowCollapsed = !sidebar.classList.contains('sidebar-collapsed');
      localStorage.setItem('sidebarCollapsedPref', nowCollapsed ? 'collapsed' : 'expanded');
      applyState();
    });

    // Re-evaluate on resize. Debounced via rAF so we don't thrash on
    // continuous drag-resize. The window matchMedia listener would be
    // cleaner but we also need to react to userspace cmd+/- zoom.
    let pending = false;
    window.addEventListener('resize', () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => { pending = false; applyState(); });
    });
  }

  // Update layout visibility based on current route
  updateLayoutVisibility() {
    const currentPath = window.location.pathname;
    const isPublicRoute = config.publicRoutes.includes(currentPath);
    const isAuth = stateManager.get('isAuthenticated');
    // Settings ("Setup") owns its own full-width layout — Salesforce-style,
    // with its own left rail of setup categories — so the regular app
    // sidebar and mobile bottom nav are redundant there. It opens in its
    // own browser tab (see the target="_blank" Settings links), so hiding
    // the main nav here never affects the app tab the user came from.
    const isSetupRoute = currentPath === '/settings' || currentPath.startsWith('/settings/');

    const sidebar = document.getElementById('sidebar');
    const header = document.querySelector('header');
    const bottomNav = document.getElementById('mobile-bottom-nav');

    // Hide sidebar, header, and bottom nav on public routes or when not authenticated.
    // Checking isAuthenticated prevents the sidebar flashing visible while the
    // async /auth/me check runs on unauthenticated page loads.
    if (isPublicRoute || !isAuth) {
      sidebar?.classList.add('!hidden');
      header?.classList.add('!hidden');
      bottomNav?.classList.add('!hidden');
    } else {
      sidebar?.classList.toggle('!hidden', isSetupRoute);
      header?.classList.remove('!hidden');
      bottomNav?.classList.toggle('!hidden', isSetupRoute);
    }
  }

  // Check authentication status
  async checkAuth() {
    const token = stateManager.get('token');

    if (token) {
      try {
        // skipUnauthorized: silently clear auth on 401 (expired/revoked
        // token) without emitting auth:logout or redirecting. The router
        // handles the redirect to /login after checkAuth() returns.
        const response = await api.get('/auth/me', {}, { skipUnauthorized: true });
        stateManager.set('user', response.data);
        stateManager.set('isAuthenticated', true);
        // Pull org-scoped settings (custom fields, statuses, branding, etc.)
        // into the localStorage cache so the synchronous static getters
        // sprinkled across pages serve fresh per-org data.
        const { default: SettingsPage } = await import('../pages/Settings.js');
        // Once org settings land in the cache, let the idle timer adopt the
        // org's configured timeout immediately instead of waiting for the
        // next user activity to re-read it.
        SettingsPage.hydrate().then(() => idleTimer.reconfigure());
        // Seed the shared currency default so pages that don't fetch
        // /appconfig themselves (Projects cards, Analytics, AccountDetail,
        // etc.) format budgets in the org's currency instead of USD.
        api.get('/appconfig', { key: 'defaultCurrency' }, { silent: true })
          .then((r) => { if (r?.data) setDefaultCurrency(r.data); })
          .catch(() => {});
        // Seed the org's active currencies (multicurrency feature) so the
        // per-record currency pickers list only those, Salesforce-style.
        loadEnabledCurrencies().catch(() => {});
      } catch (error) {
        console.error('Auth check failed:', error);
        stateManager.clearAuth();
      }
    }
  }

  // Register all routes
  registerRoutes() {
    // Public routes
    router.register('/login', async () => {
      const { default: LoginPage } = await import('../pages/Login.js');
      return new LoginPage();
    });

    router.register('/signup', async () => {
      const { default: SignupPage } = await import('../pages/Signup.js');
      return new SignupPage();
    });

    router.register('/verify-email', async () => {
      const { default: Page } = await import('../pages/VerifyEmail.js');
      return new Page();
    });

    router.register('/forgot-password', async () => {
      const { default: Page } = await import('../pages/ForgotPassword.js');
      return new Page();
    });

    router.register('/reset-password', async () => {
      const { default: Page } = await import('../pages/ResetPassword.js');
      return new Page();
    });

    router.register('/shared/:token', async (params) => {
      const { default: Page } = await import('../pages/SharedLink.js');
      return new Page(params);
    });

    // Operator (super-admin) routes — separate auth via pm_operator_token.
    // Lives at /operator/* (the prior /admin/* routes are kept as
    // backward-compat redirects so old bookmarks still work).
    router.register('/operator/login', async () => {
      const { default: OperatorLoginPage } = await import('../pages/OperatorLogin.js');
      return new OperatorLoginPage();
    });

    router.register('/operator', async () => {
      if (!localStorage.getItem('pm_operator_token')) {
        router.navigate('/operator/login');
        return null;
      }
      const { default: OperatorPage } = await import('../pages/Operator.js');
      return new OperatorPage();
    });

    router.register('/operator/orgs/:id', async (params) => {
      if (!localStorage.getItem('pm_operator_token')) {
        router.navigate('/operator/login');
        return null;
      }
      const { default: OperatorOrgDetailPage } = await import('../pages/OperatorOrgDetail.js');
      return new OperatorOrgDetailPage(params.id);
    });

    // Backward-compat aliases for the old /admin paths. Use replace so
    // the deprecated URL doesn't leave a phantom step in the back stack.
    router.register('/admin/login', async () => { router.navigate('/operator/login', { replace: true }); return null; });
    router.register('/admin', async () => { router.navigate('/operator', { replace: true }); return null; });
    router.register('/admin/orgs/:id', async (params) => { router.navigate(`/operator/orgs/${params.id}`, { replace: true }); return null; });

    // Protected routes
    router.register('/dashboard', async () => {
      const { default: DashboardPage } = await import('../pages/Dashboard.js');
      return new DashboardPage();
    });

    router.register('/my-work', async () => {
      const { default: MyWorkPage } = await import('../pages/MyWork.js');
      return new MyWorkPage();
    });

    router.register('/bookmarks', async () => {
      const { default: BookmarksPage } = await import('../pages/Bookmarks.js');
      return new BookmarksPage();
    });

    router.register('/search', async () => {
      const { default: SearchPage } = await import('../pages/Search.js');
      return new SearchPage();
    });

    // CSV data import — dedicated page. /import/:entity deep-links with an
    // object preselected (used by the per-list-page "Import" buttons).
    router.register('/import', async () => {
      const { default: DataImportPage } = await import('../pages/DataImport.js');
      return new DataImportPage();
    });

    router.register('/import/:entity', async (params) => {
      const { default: DataImportPage } = await import('../pages/DataImport.js');
      return new DataImportPage(params.entity);
    });

    router.register('/projects', async () => {
      const { default: ProjectsPage } = await import('../pages/Projects.js');
      return new ProjectsPage();
    });

    router.register('/projects/:id', async (params) => {
      const { default: ProjectDetailPage } = await import('../pages/ProjectDetail.js');
      return new ProjectDetailPage(params.id);
    });

    router.register('/teams', async () => {
      const { default: TeamsPage } = await import('../pages/Teams.js');
      return new TeamsPage();
    });

    router.register('/teams/:id', async (params) => {
      const { default: TeamDetailPage } = await import('../pages/TeamDetail.js');
      return new TeamDetailPage(params.id);
    });

    router.register('/tasks', async () => {
      const { default: TasksPage } = await import('../pages/Tasks.js');
      return new TasksPage();
    });

    router.register('/tasks/:id', async (params) => {
      const { default: TaskDetailPage } = await import('../pages/TaskDetail.js');
      return new TaskDetailPage(params.id);
    });

    router.register('/sprints', async () => {
      const { default: SprintsPage } = await import('../pages/Sprints.js');
      return new SprintsPage();
    });

    router.register('/sprints/:id', async (params) => {
      const { default: SprintDetailPage } = await import('../pages/SprintDetail.js');
      return new SprintDetailPage(params.id);
    });

    router.register('/gantt', async () => {
      const { default: GanttPage } = await import('../pages/GanttView.js');
      return new GanttPage();
    });

    router.register('/gantt/:projectId', async (params) => {
      const { default: GanttPage } = await import('../pages/GanttView.js');
      return new GanttPage(params.projectId);
    });

    router.register('/calendar', async () => {
      const { default: CalendarPage } = await import('../pages/Calendar.js');
      return new CalendarPage();
    });

    router.register('/timesheets', async () => {
      const { default: TimesheetsPage } = await import('../pages/Timesheets.js');
      return new TimesheetsPage();
    });

    router.register('/time-off', async () => {
      const { default: TimeOffPage } = await import('../pages/TimeOff.js');
      return new TimeOffPage();
    });

    router.register('/time-off/:id', async (params) => {
      const { default: TimeOffDetailPage } = await import('../pages/TimeOffDetail.js');
      return new TimeOffDetailPage(params.id);
    });

    router.register('/expenses', async () => {
      const { default: ExpensesPage } = await import('../pages/Expenses.js');
      return new ExpensesPage();
    });

    router.register('/expenses/:id', async (params) => {
      const { default: ExpenseDetailPage } = await import('../pages/ExpenseDetail.js');
      return new ExpenseDetailPage(params.id);
    });

    router.register('/approvals', async () => {
      const { default: ApprovalsPage } = await import('../pages/Approvals.js');
      return new ApprovalsPage();
    });

    router.register('/accounts', async () => {
      const { default: AccountsPage } = await import('../pages/Accounts.js');
      return new AccountsPage();
    });

    router.register('/accounts/:id', async (params) => {
      const { default: AccountDetailPage } = await import('../pages/AccountDetail.js');
      return new AccountDetailPage(params.id);
    });

    router.register('/contacts', async () => {
      const { default: ContactsPage } = await import('../pages/Contacts.js');
      return new ContactsPage();
    });

    router.register('/contacts/:id', async (params) => {
      const { default: ContactDetailPage } = await import('../pages/ContactDetail.js');
      return new ContactDetailPage(params.id);
    });

    router.register('/deals', async () => {
      const { default: DealsPage } = await import('../pages/Deals.js');
      return new DealsPage();
    });

    router.register('/deals/:id', async (params) => {
      const { default: DealDetailPage } = await import('../pages/DealDetail.js');
      return new DealDetailPage(params.id);
    });

    router.register('/invoices', async () => {
      const { default: InvoicesPage } = await import('../pages/Invoices.js');
      return new InvoicesPage();
    });

    router.register('/invoices/:id', async (params) => {
      const { default: InvoiceDetailPage } = await import('../pages/InvoiceDetail.js');
      return new InvoiceDetailPage(params.id);
    });

    router.register('/resources', async () => {
      const { default: ResourcePlannerPage } = await import('../pages/ResourcePlanner.js');
      return new ResourcePlannerPage();
    });

    router.register('/people/:userId', async (params) => {
      const { default: ResourceDetailPage } = await import('../pages/ResourceDetail.js');
      return new ResourceDetailPage(params.userId);
    });

    router.register('/analytics', async () => {
      const { default: AnalyticsPage } = await import('../pages/Analytics.js');
      return new AnalyticsPage();
    });

    router.register('/settings', async () => {
      const { default: SettingsPage } = await import('../pages/Settings.js');
      return new SettingsPage();
    });

    router.register('/audit-log', async () => {
      const { default: AuditLogPage } = await import('../pages/AuditLog.js');
      return new AuditLogPage();
    });

    router.register('/recycle-bin', async () => {
      const { default: RecycleBinPage } = await import('../pages/RecycleBin.js');
      return new RecycleBinPage();
    });

    router.register('/settings/roles/new', async () => {
      const { default: RoleDetailPage } = await import('../pages/RoleDetail.js');
      return new RoleDetailPage('new');
    });

    router.register('/settings/roles/:id', async (params) => {
      const { default: RoleDetailPage } = await import('../pages/RoleDetail.js');
      return new RoleDetailPage(params.id);
    });

    router.register('/profile', async () => {
      const { default: ProfilePage } = await import('../pages/Profile.js');
      return new ProfilePage();
    });

    router.register('/users/:id', async (params) => {
      const { default: UserDetailPage } = await import('../pages/UserDetail.js');
      return new UserDetailPage(params.id);
    });

    router.register('/automations', async () => {
      const { default: AutomationsPage } = await import('../pages/Automations.js');
      return new AutomationsPage();
    });

    router.register('/holidays', async () => {
      const { default: HolidaysPage } = await import('../pages/Holidays.js');
      return new HolidaysPage();
    });

    router.register('/attendance', async () => {
      const { default: AttendancePage } = await import('../pages/Attendance.js');
      return new AttendancePage();
    });

    router.register('/attendance/:id', async (params) => {
      const { default: AttendanceDetailPage } = await import('../pages/AttendanceDetail.js');
      return new AttendanceDetailPage(params.id);
    });

    // Coming Soon placeholders for planned modules
    const comingSoonPage = (title, description, icon) => {
      document.getElementById('main-content').innerHTML = `
          <div class="flex flex-col items-center justify-center h-full gap-4 text-center px-6">
            <div class="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
              <i class="${icon} text-2xl text-blue-400"></i>
            </div>
            <h1 class="text-2xl font-bold text-gray-800">${title}</h1>
            <p class="text-gray-500 max-w-sm text-sm">${description}</p>
            <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold">
              <i class="fas fa-clock text-[10px]"></i> Coming Soon
            </span>
          </div>`;
      return { destroy() {} };
    };
    router.register('/leads', async () => comingSoonPage('Leads', 'Capture and track potential customers before they become deals.', 'fas fa-funnel-dollar'));
    router.register('/inventory', async () => comingSoonPage('Inventory', 'Manage your products, stock levels, and warehouse locations.', 'fas fa-boxes'));
    router.register('/customers', async () => comingSoonPage('Customers', 'A dedicated view of your converted customers and their history.', 'fas fa-user-tie'));
    router.register('/vehicles', async () => comingSoonPage('Vehicles', 'Track your fleet, maintenance schedules, and assignments.', 'fas fa-truck'));
  }
}

// Initialize app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new App());
} else {
  new App();
}

export default App;
